import styled, { keyframes, css } from "styled-components";
import { useState, useRef, useEffect } from "react";
import { usePage } from "@inertiajs/react";
import { ChevronDown } from "lucide-react";

import useReducedMotion from "../_kit/useReducedMotion";
import richText from "../_kit/richText";
import Media from "../_kit/Media";
import Button from "../_kit/Button";
import { propertyAbout } from "../../../components/insights/paths";
import { useLandingLanguage } from "../../../contexts/landing-language";
import {
  z,
  weight,
  type,
  tracking,
  motion,
  media,
  leading,
  font,
  color,
} from "../../../styles/tokens";

/* The hero carries one of two media modes, chosen in the CMS by `hero_media_type`.
 *
 * It previously auto-rotated TWENTY-SIX images on a fixed 5s timer, as a CSS background-image on a
 * pseudo-element: no alt text, no srcset, no lazy loading, unpausable, and with a
 * `transition: background-image` that has never done anything because background-image is not an
 * interpolable property — every slide was a hard cut.
 */

const SLIDE_MS = 6000;

/* A hero is a glance, not a slideshow. Past about five, slides are bytes nobody sees. */
const MAX_SLIDES = 5;

const kenBurns = keyframes`
  from { transform: scale(1); }
  to   { transform: scale(1.06); }
`;

const HeroSection = styled.section`
  position: relative;
  /* A full screen of video, edge to edge. No max-height: the point is that it fills whatever
   * display it lands on.
   *
   * dvh, not vh: on mobile Safari vh is the LARGEST viewport, so the CTAs sat under the browser
   * chrome until the address bar collapsed. */
  height: 100dvh;
  min-height: 640px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  isolation: isolate;
`;

const MediaLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: -2;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Slide = styled.div`
  position: absolute;
  inset: 0;
  opacity: ${(props) => (props.$active ? 1 : 0)};
  /* A real crossfade, on opacity — the property the old code was trying to transition. */
  transition: opacity 1200ms ease-in-out;

  > div {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  /* The css helper, not a bare template string: a keyframes object interpolated into an untagged
   * string throws during SSR, and inertia_rails swallows that into a silent client-only fallback. */
  ${(props) =>
    props.$active && props.$animate
      ? css`
          animation: ${kenBurns} 18s ease-out forwards;
        `
      : ""}
`;

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background: ${color.scrim};
`;

const Content = styled.div`
  position: relative;
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 clamp(20px, 5vw, 40px) clamp(80px, 12vh, 140px);
  color: ${color.onDark};
`;

const Eyebrow = styled.p`
  margin: 0 0 20px;
  font-family: ${font.body};
  font-size: ${type.eyebrow};
  font-weight: ${weight.semibold};
  letter-spacing: ${tracking.eyebrow};
  text-transform: uppercase;
  color: ${color.gold};
`;

const Name = styled.h1`
  margin: 0 0 16px;
  max-width: 16ch;
  font-family: ${font.display};
  font-size: ${type.display};
  font-weight: ${weight.regular};
  line-height: ${leading.tight};
  letter-spacing: ${tracking.tight};
  color: ${color.onDark};
`;

/* The CMS heading. A <p>, not an <h2> — the old markup put this ABOVE the <h1>, so every page had
 * its heading order inverted. Editors use <strong> here, so the value is sanitised, not escaped.
 */
const Tagline = styled.p`
  margin: 0 0 20px;
  max-width: 28ch;
  font-family: ${font.display};
  font-size: ${type.h3};
  font-weight: ${weight.regular};
  line-height: ${leading.snug};
  color: ${color.onDark};

  strong {
    font-weight: ${weight.semibold};
  }
`;

const Lede = styled.p`
  margin: 0 0 40px;
  max-width: 52ch;
  font-size: ${type.lg};
  line-height: ${leading.relaxed};
  color: ${color.onDarkMuted};

  ${media.md`
    font-size: ${type.base};
  `}
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  ${media.sm`
    flex-direction: column;
  `}
`;

const Dots = styled.div`
  position: absolute;
  right: clamp(20px, 5vw, 40px);
  bottom: clamp(80px, 12vh, 140px);
  display: flex;
  gap: 10px;
  z-index: ${z.base};
`;

const Dot = styled.button`
  width: 32px;
  height: 3px;
  padding: 0;
  border: 0;
  cursor: pointer;
  background: ${(props) => (props.$active ? color.gold : "rgba(246, 244, 238, 0.4)")};
  transition: background ${motion.base};

  &:hover {
    background: ${color.gold};
  }
`;

const ScrollHint = styled.a`
  position: absolute;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  color: ${color.onDarkMuted};
  transition: color ${motion.base};

  &:hover {
    color: ${color.onDark};
  }

  ${media.md`
    display: none;
  `}
`;

const Hero = (props) => {
  const { landing_content } = usePage().props;
  const { t, language } = useLandingLanguage();
  const copy = t.hero;

  const mode =
    landing_content?.hero_media_type === "video" ? "video" : "images";
  const images = (landing_content?.hero_banner_images || []).slice(
    0,
    MAX_SLIDES,
  );
  const videoUrl = landing_content?.hero_video_url;
  const poster = landing_content?.hero_video_poster;

  // Fall back to the carousel if an editor flips the switch before uploading a file.
  const showVideo = mode === "video" && Boolean(videoUrl);

  const reducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (showVideo || reducedMotion || paused || images.length <= 1)
      return undefined;

    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % images.length);
    }, SLIDE_MS);
    return () => clearInterval(timer);
  }, [showVideo, reducedMotion, paused, images.length]);

  /* Playback is started here rather than with an autoPlay attribute, for two reasons: the server
   * cannot know the motion preference, so an attribute would either lie or break hydration; and a
   * muted programmatic play() is permitted by every autoplay policy. With JS off, or under reduced
   * motion, the poster simply stays — which is the correct outcome, not a degraded one.
   */
  useEffect(() => {
    const element = videoRef.current;
    if (!element || !showVideo) return;

    if (reducedMotion) {
      element.pause();
      return;
    }
    const attempt = element.play();
    if (attempt && typeof attempt.catch === "function") attempt.catch(() => {});
  }, [showVideo, reducedMotion]);

  return (
    <HeroSection id="home">
      <MediaLayer>
        {showVideo ? (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload="metadata"
            poster={poster?.url}
            aria-label={copy.videoLabel}
            tabIndex={-1}
          >
            <source
              src={videoUrl}
              type={landing_content?.hero_video_mime || "video/mp4"}
            />
          </video>
        ) : (
          images.map((image, slideIndex) => (
            <Slide
              key={image.url}
              $active={slideIndex === index}
              $animate={slideIndex === index && !reducedMotion}
              aria-hidden={slideIndex !== index}
            >
              <Media
                source={image}
                alt={image.alt_text || ""}
                sizes="100vw"
                priority={slideIndex === 0}
              />
            </Slide>
          ))
        )}
      </MediaLayer>
      <Scrim />

      <Content>
        <Eyebrow>{copy.eyebrow}</Eyebrow>
        <Name>{copy.name}</Name>
        {landing_content?.heading ? (
          <Tagline>{richText(landing_content.heading)}</Tagline>
        ) : null}
        {landing_content?.subheading ? (
          <Lede>{landing_content.subheading}</Lede>
        ) : null}

        <Actions>
          <Button as="a" href="#booking" $variant="primary" $size="lg">
            {copy.bookStay}
          </Button>
          <Button
            as="a"
            href={propertyAbout(language)}
            $variant="onDark"
            $size="lg"
          >
            {copy.discover}
          </Button>
        </Actions>
      </Content>

      {!showVideo && images.length > 1 ? (
        <Dots
          role="group"
          aria-label={copy.galleryLabel}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {images.map((image, slideIndex) => (
            <Dot
              key={image.url}
              type="button"
              $active={slideIndex === index}
              aria-current={slideIndex === index}
              aria-label={copy.slideLabel
                .replace("{n}", slideIndex + 1)
                .replace("{total}", images.length)}
              onFocus={() => setPaused(true)}
              onBlur={() => setPaused(false)}
              onClick={() => setIndex(slideIndex)}
            />
          ))}
        </Dots>
      ) : null}

      <ScrollHint href="#booking" aria-label={copy.scrollHint}>
        <ChevronDown size={28} aria-hidden="true" />
      </ScrollHint>
    </HeroSection>
  );
};

export default Hero;
